import type { ProductLeaf, PropertyValue } from "@decocms/apps-commerce/types";
import { useOffer } from "~/sdk/useOffer";

export type Possibilities = Record<string, Record<string, string | undefined>>;

const hash = ({ name, value }: PropertyValue) => `${name}::${value}`;

const omit = new Set(["category", "cluster", "RefId", "descriptionHtml", "modalType", "Modal Type"]);

// A VTEX não garante a mesma ordem de `additionalProperty` entre variantes do
// mesmo produto — uma variante pode listar "Cor" antes de "Tamanho", outra o
// inverso. A lógica abaixo assume que specs[0] é sempre a dimensão primária
// (sempre selecionável); sem uma ordem fixa, "primária" muda de variante pra
// variante e corrompe o mapa de possibilidades (tamanhos ficam presos como
// indisponíveis, ou apontam pra URL errada).
const DIMENSION_PRIORITY = ["Cor", "Tamanho"];

const sortSpecs = (specs: PropertyValue[]) =>
  [...specs].sort((a, b) => {
    const rankOf = (spec: PropertyValue) => {
      const index = DIMENSION_PRIORITY.indexOf(spec.name!);
      return index === -1 ? DIMENSION_PRIORITY.length : index;
    };
    return rankOf(a) - rankOf(b);
  });

export const useVariantPossibilities = (
  variants: ProductLeaf[],
  selected: ProductLeaf,
): Possibilities => {
  const possibilities: Possibilities = {};
  const selectedSpecs = new Set(selected.additionalProperty?.map(hash));

  for (const variant of variants) {
    const { url, additionalProperty = [], productID, offers } = variant;
    const isSelected = productID === selected.productID;
    const specs = sortSpecs(
      additionalProperty.filter(({ name }) => !omit.has(name!)),
    );
    const { availability } = useOffer(offers);

    for (let it = 0; it < specs.length; it++) {
      const name = specs[it].name!;
      const value = specs[it].value!;

      if (omit.has(name)) continue;

      if (!possibilities[name]) {
        possibilities[name] = {};
      }

      // First row is always selectable
      const isSelectable = it === 0 ||
        specs.every((s) => s.name === name || selectedSpecs.has(hash(s)));

      const availabilityStatus = availability === "https://schema.org/InStock"
        ? "AVAILABLE"
        : "NOT_AVAILABLE";

      const selectedStatus = isSelected ? "SELECTED" : "NOT_SELECTED";

      const composedUrl = `${availabilityStatus}::${selectedStatus}::${url}`;
      const currentUrl = possibilities[name][value];

      possibilities[name][value] = isSelected
        ? composedUrl
        : isSelectable
        ? currentUrl?.includes("NOT_SELECTED") &&
            currentUrl?.includes("NOT_AVAILABLE")
          ? composedUrl
          : currentUrl || composedUrl
        : currentUrl;
    }
  }

  return possibilities;
};
